// Programs index — folder overview + all programs list + bookings.
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../../shared/api/client";
import { I } from "../../shared/Icon";
import { toast } from "../../shared/stores/toasts";
import { useAuth } from "../../shared/stores/auth";
import { UuidField, pageTypeIcon, pageTypeLabel } from "../../shell/ProgramsSidebar";
import { IconPicker, DEFAULT_ICON } from "../../shared/components/IconPicker";

// ── Types ─────────────────────────────────────────────────────────────────────

type Program = {
  id: number; name: string; category: string; description: string | null;
  is_active: boolean; page_type: string; uuid: string | null; folder_id: number | null;
  icon: string | null;
  services: { id: number; is_active: boolean }[];
};
type Booking = {
  id: number; service_id: number; customer_id: number;
  scheduled_at: string | null; status: string; note: string | null;
  service_name: string; program_name: string;
};

function isAdmin(user: any): boolean {
  const roles: string[] = user?.roles ?? user?.role_codes ?? [];
  return roles.includes("SUPER_ADMIN") || roles.includes("ADMIN");
}

// ── Status helpers ────────────────────────────────────────────────────────────

const STATUS_KO: Record<string, string> = { PENDING: "대기", CONFIRMED: "확정", IN_PROGRESS: "진행 중", COMPLETED: "완료", CANCELLED: "취소" };
const STATUS_COLOR: Record<string, string> = { PENDING: "#D97706", CONFIRMED: "#2563EB", IN_PROGRESS: "#7C3AED", COMPLETED: "#16A34A", CANCELLED: "#9CA3AF" };
const smBtn: React.CSSProperties = { fontSize: 11, padding: "3px 10px", borderRadius: 5, border: "1px solid var(--border)", background: "none", color: "var(--text-2)", cursor: "pointer" };

// ── New program modal ─────────────────────────────────────────────────────────

function NewProgramModal({ folderId, onClose, onCreated }: {
  folderId: number | null; onClose: () => void; onCreated: (prog: { id: number; uuid: string }) => void;
}) {
  const [name, setName] = useState("");
  const [uuid, setUuid] = useState("");
  const [icon, setIcon] = useState(DEFAULT_ICON);
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      const res = await api.post<{ id: number; uuid: string }>("/programs", {
        name: name.trim(), page_type: "custom", icon, folder_id: folderId, category: "기타",
        uuid: uuid.trim() || undefined,
      });
      onCreated(res.data);
    } catch (e: any) { toast.error("추가 실패", e?.response?.data?.error?.message ?? ""); }
    finally { setSaving(false); }
  };

  return (
    <div className="t-overlay" onClick={onClose}>
      <div className="t-cmdk" onClick={e => e.stopPropagation()} style={{ width: 420, maxWidth: "95vw" }}>
        <div style={{ padding: "14px 16px 10px", borderBottom: "1px solid var(--border)", display: "flex", alignItems: "center", gap: 8 }}>
          <h3 style={{ margin: 0, fontSize: 14, flex: 1 }}>새 프로그램</h3>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 18, color: "var(--text-3)" }}>✕</button>
        </div>
        <div style={{ padding: 16, display: "grid", gap: 14 }}>
          <div>
            <label className="t-label">이름 *</label>
            <input className="t-input" autoFocus value={name} onChange={e => setName(e.target.value)} onKeyDown={e => e.key === "Enter" && submit()} placeholder="프로그램 이름" />
          </div>

          <UuidField uuid={uuid} setUuid={setUuid} />

          <div>
            <label className="t-label" style={{ display: "flex", alignItems: "center", gap: 6 }}>
              아이콘 <span style={{ fontSize: 18 }}>{icon}</span>
            </label>
            <IconPicker value={icon} onChange={setIcon} />
          </div>
        </div>
        <div style={{ padding: "10px 16px", borderTop: "1px solid var(--border)", display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button className="t-btn" onClick={onClose} disabled={saving}>취소</button>
          <button className="t-btn t-btn-primary" onClick={submit} disabled={saving || !name.trim()}>{saving ? "추가 중…" : "추가"}</button>
        </div>
      </div>
    </div>
  );
}

// ── Program card (index/grid item) ────────────────────────────────────────────

function ProgramCard({ prog, admin }: {
  prog: Program; admin: boolean;
}) {
  const nav = useNavigate();
  const [hovered, setHovered] = useState(false);
  const serviceCount = prog.services.filter(s => s.is_active || admin).length;

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={() => nav(`/programs/${encodeURIComponent(prog.uuid ?? String(prog.id))}`)}
      style={{ border: "1px solid var(--border)", borderRadius: 10, padding: 16, cursor: "pointer", background: "var(--bg-0)", transition: "all .15s", boxShadow: hovered ? "0 4px 16px rgba(0,0,0,0.1)" : "none", transform: hovered ? "translateY(-1px)" : "none", position: "relative" }}
    >
      <div style={{ display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 10 }}>
        <div style={{ width: 40, height: 40, borderRadius: 10, background: "rgba(99,102,241,0.08)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, flexShrink: 0 }}>
          {prog.icon || pageTypeIcon(prog.page_type)}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: "var(--text-1)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{prog.name}</div>
          <div style={{ fontSize: 11, color: "var(--p-500,#6366f1)", marginTop: 2, fontWeight: 600 }}>{pageTypeLabel(prog.page_type)}</div>
        </div>
        {!prog.is_active && (
          <span style={{ fontSize: 10, padding: "2px 6px", borderRadius: 8, background: "#fef3c7", color: "#d97706", fontWeight: 700, flexShrink: 0 }}>비활성</span>
        )}
      </div>

      {prog.description && (
        <p style={{ margin: "0 0 10px", fontSize: 12, color: "var(--text-3)", lineHeight: 1.6, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
          {prog.description}
        </p>
      )}

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: "auto" }}>
        {prog.page_type === "catalog" ? (
          <span style={{ fontSize: 11, color: "var(--text-3)" }}>서비스 {serviceCount}개</span>
        ) : (
          <span style={{ fontSize: 11, color: "var(--text-3)" }}>{pageTypeLabel(prog.page_type)}</span>
        )}
      </div>
    </div>
  );
}

// ── Catalog grid ──────────────────────────────────────────────────────────────

function ProgramGrid({ folderId, admin }: { folderId: number | null; admin: boolean }) {
  const nav = useNavigate();
  const [programs, setPrograms] = useState<Program[] | null>(null);
  const [adding, setAdding] = useState(false);

  const load = () => {
    const q = folderId != null ? `?folder_id=${folderId}` : "";
    api.get<Program[]>(`/programs${q}`)
      .then(r => setPrograms(r.data))
      .catch(() => setPrograms([]));
  };

  useEffect(() => { setPrograms(null); load(); }, [folderId]);

  if (programs === null) return <div style={{ padding: 40, textAlign: "center", color: "var(--text-3)" }}>불러오는 중…</div>;

  return (
    <>
      {admin && (
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 16 }}>
          <button className="t-btn t-btn-primary" onClick={() => setAdding(true)}>
            <I.Plus size={13}/> 새 프로그램
          </button>
        </div>
      )}

      {programs.length === 0 && (
        <div style={{ padding: "60px 0", textAlign: "center", color: "var(--text-3)" }}>
          <div style={{ fontSize: 36, marginBottom: 12 }}>🗂️</div>
          <div style={{ fontSize: 14, fontWeight: 500 }}>프로그램이 없습니다</div>
          {admin && <div style={{ fontSize: 12, marginTop: 4 }}>버튼을 눌러 새 프로그램을 추가하세요.</div>}
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: 14 }}>
        {programs.map(prog => (
          <ProgramCard key={prog.id} prog={prog} admin={admin} />
        ))}
      </div>

      {adding && (
        <NewProgramModal
          folderId={folderId}
          onClose={() => setAdding(false)}
          onCreated={prog => { setAdding(false); nav(`/programs/${encodeURIComponent(prog.uuid ?? String(prog.id))}`); }}
        />
      )}
    </>
  );
}

// ── My bookings ───────────────────────────────────────────────────────────────

function MyBookings() {
  const [bookings, setBookings] = useState<Booking[] | null>(null);
  const [filter, setFilter] = useState("");

  const load = () =>
    api.get<Booking[]>(`/programs/bookings/me${filter ? `?status=${filter}` : ""}`)
      .then(r => setBookings(r.data))
      .catch(() => setBookings([]));

  useEffect(() => { load(); }, [filter]);

  const cancel = async (id: number) => {
    try {
      await api.patch(`/programs/bookings/${id}`, { status: "CANCELLED" });
      toast.success("취소됨"); load();
    } catch { toast.error("취소 실패"); }
  };

  return (
    <div>
      <div style={{ display: "flex", gap: 6, marginBottom: 20, flexWrap: "wrap" }}>
        {(["", "PENDING", "CONFIRMED", "IN_PROGRESS", "COMPLETED", "CANCELLED"] as const).map(s => (
          <button key={s} onClick={() => setFilter(s)}
            style={{ padding: "5px 14px", borderRadius: 20, fontSize: 12, border: `1.5px solid ${filter === s ? "var(--p-400)" : "var(--border)"}`, background: filter === s ? "var(--p-50,#eef2ff)" : "transparent", color: filter === s ? "var(--p-600)" : "var(--text-2)", cursor: "pointer" }}>
            {s ? (STATUS_KO[s] ?? s) : "전체"}
          </button>
        ))}
      </div>

      {bookings === null && <div style={{ textAlign: "center", color: "var(--text-3)", padding: 40 }}>불러오는 중…</div>}
      {bookings?.length === 0 && (
        <div style={{ padding: "60px 0", textAlign: "center", color: "var(--text-3)" }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>📋</div>
          <div style={{ fontSize: 14 }}>예약 내역이 없습니다</div>
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {bookings?.map(b => (
          <div key={b.id} style={{ border: "1px solid var(--border)", borderRadius: 8, padding: "12px 14px", display: "flex", alignItems: "center", gap: 12, background: "var(--bg-0)" }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: 13 }}>{b.service_name}</div>
              <div style={{ fontSize: 11, color: "var(--text-3)", marginTop: 2 }}>{b.program_name}</div>
              {b.scheduled_at && <div style={{ fontSize: 11, color: "var(--text-3)", marginTop: 1 }}>예약일: {new Date(b.scheduled_at).toLocaleDateString("ko-KR")}</div>}
            </div>
            <span style={{ padding: "3px 10px", borderRadius: 10, fontSize: 11, fontWeight: 600, background: `${STATUS_COLOR[b.status] ?? "#9CA3AF"}18`, color: STATUS_COLOR[b.status] ?? "#9CA3AF" }}>
              {STATUS_KO[b.status] ?? b.status}
            </span>
            {b.status === "PENDING" && (
              <button onClick={() => cancel(b.id)} style={{ ...smBtn, color: "#ef4444", borderColor: "#ef4444" }}>취소</button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function ProgramsPage() {
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const admin = isAdmin(user);

  const folderId    = searchParams.get("folder") ? Number(searchParams.get("folder")) : null;
  const showBookings = searchParams.get("filter") === "bookings";

  const [folderName, setFolderName] = useState<string | null>(null);

  useEffect(() => {
    if (folderId == null) { setFolderName(null); return; }
    api.get<any[]>("/programs/folders").then(r => {
      const flat: { id: number; name: string }[] = [];
      const walk = (arr: any[]) => arr.forEach((f: any) => { flat.push(f); walk(f.children ?? []); });
      walk(r.data);
      setFolderName(flat.find(f => f.id === folderId)?.name ?? null);
    }).catch(() => {});
  }, [folderId]);

  return (
    <div style={{ padding: "20px 24px", maxWidth: 960, margin: "0 auto" }}>
      {/* Title */}
      <div style={{ marginBottom: 20 }}>
        <h2 style={{ margin: "0 0 4px", fontSize: 18, fontWeight: 700, color: "var(--text-1)" }}>
          {showBookings ? "📋 내 예약 내역" : folderName ? `📁 ${folderName}` : "🗂️ 전체 서비스"}
        </h2>
        {!showBookings && (
          <p style={{ margin: 0, fontSize: 12, color: "var(--text-3)" }}>
            {folderName
              ? `"${folderName}" 폴더의 프로그램 목록 — 클릭하면 상세 페이지로 이동합니다`
              : "등록된 모든 프로그램 목록 — 클릭하면 상세 페이지로 이동합니다"}
          </p>
        )}
      </div>

      {showBookings
        ? <MyBookings />
        : <ProgramGrid folderId={folderId} admin={admin} />}
    </div>
  );
}
